// Cross-package schema for the **stroke log segment** file format
// (絵ログ / タイムラプス基盤 T0 — docs/_wip/growth-timelapse-share-2026-07-02.md §4-§5).
//
// A stroke log segment is a JSON document the server PUTs to object storage
// under `{roomId}/log/{fromSeq}-{toSeq}.json` every time a snapshot publish
// trims the session buffer (+ a final flush on graceful shutdown). It carries
// the room's **canvas-affecting broadcast events** verbatim — the same
// server-stamped shapes that ride the WS wire — so a client can download the
// segments and deterministically replay the whole drawing history through
// the shared drawing-core (bit-perfect, same guarantee as snapshots).
//
// This is a **file format**, not a WS event: it lives in `shared/` next to
// `snapshot.ts` (the meta.json schema) for the same reason — server-side
// writer (`AwsS3StrokeLogStore`) and client-side reader (T1 replay) must
// agree on field names/types, and a drift would be a silent replay
// corruption bug.
//
// ─── Consumer rules (T1 replay) ────────────────────────────────────────────
//
//   - **Coverage range**: a segment covers seqs `fromSeq..toSeq` inclusive.
//     Events inside are in ascending `seq` order but may be sparse — the
//     room's seq counter is shared with non-drawing broadcasts (chat /
//     member) which are NOT logged.
//   - **Contiguity / gap detection**: with no data loss, consecutive
//     segments satisfy `next.fromSeq === prev.toSeq + 1`. A hole in the
//     coverage means events were lost (flush PUT failure or process crash);
//     replay should tolerate it (drawing-core drops orphan stroke
//     segments/ends whose start is missing).
//   - **Overlap resolution**: after a WS server restart the room's seq
//     counter resumes from the last *snapshot* seq, so events flushed by a
//     pre-restart final flush can share seq numbers with post-restart
//     events (= overlapping segment ranges). When ranges overlap, the
//     segment with the **larger `flushedAtMs` wins** for the shared seqs —
//     the post-restart events are the canonical timeline (the pre-restart
//     tail was never baked into any snapshot either).
//   - **Timestamps**: every event carries the server-stamped `ts` (epoch ms
//     at broadcast time = receive time). This is what the timelapse 倍速
//     mode consumes (§5.2 — 実描画時間ベースの ×N 倍速 + 間隔クランプ).
//   - **imagepaste caveat**: `whiteboard.imagepaste.url` is a presigned GET
//     URL that expires ~30 min after broadcast. Replaying an old segment
//     needs the URL re-signed (T1 follow-up); until then replay may render
//     the paste as a no-op.
import { z } from 'zod';
import { strokeCancelledBroadcastSchema, strokeEndBroadcastSchema, strokeSegmentBroadcastSchema, strokeStartBroadcastSchema, } from '../events/stroke.js';
import { whiteboardBackgroundSetBroadcastSchema, whiteboardClearBroadcastSchema, whiteboardImagepasteSchema, whiteboardRegionCancelledSchema, whiteboardRegionSchema, whiteboardRestoreSchema, } from '../events/whiteboard.js';
/**
 * Current segment document version. Bump on breaking shape changes so a
 * future reader can discriminate (same policy as `snapshotMetaSchema`'s
 * `metaVersion`, but required from day one because there is no legacy
 * corpus to stay compatible with).
 */
export const STROKE_LOG_SEGMENT_VERSION = 1;
/**
 * Events that appear in a stroke log segment — exactly the "drawing-only"
 * subset the session buffer persists (`protocol-events.md` §2.10 +
 * `whiteboard.background-set`, mirroring `Broadcaster.isDrawingEvent`):
 *
 *   - `stroke.start` / `stroke.segment` / `stroke.end`
 *   - `stroke.cancelled` (conflict-aware Undo の tombstone、2026-07-22 確定.
 *     seq 付き描画イベントとしてログに焼き込まれるため、タイムラプス再生は
 *     「描いて消した」も再現する. undo-conflict-aware-2026-07-12.md f1)
 *   - `whiteboard.clear`
 *   - `whiteboard.imagepaste`
 *   - `whiteboard.region` (範囲の移動・コピー. imagepaste と同じく `items[].url` は
 *     presigned GET で ~30 分で期限切れ → replay は basename `{uploadId}.png` を再署名)
 *   - `whiteboard.region-cancelled` (範囲の移動・コピーの取り消しの tombstone. `stroke.cancelled`
 *     と同じ扱い。`regionSeq` が指す region を「描いて消した」として再現する)
 *   - `whiteboard.background-set`
 *   - `whiteboard.restore` (過去キャンバス復元 Slice 3,
 *     past-canvas-new-document-2026-07-05.md §4.5-1: **系譜マーカー**として
 *     ログに焼き込む. `restoredFromSessionId` / `restoredFromSeq` の耐久 ID
 *     だけでタイムラプス T1 が復元元ドキュメントの seq 範囲を辿れる = ログ
 *     単体で系譜が自己完結する. `snapshot` の presigned URL は TTL 60s で
 *     期限切れするため replay では使わない — imagepaste caveat と同型だが、
 *     restore は耐久 ID で完全再構築できる点で強い)
 *
 * All variants are the **server-stamped broadcast shapes** (`uid` / `seq` /
 * `ts` present). chat / member / room / system events are never logged.
 */
export const strokeLogEventSchema = z.discriminatedUnion('t', [
    strokeStartBroadcastSchema,
    strokeSegmentBroadcastSchema,
    strokeEndBroadcastSchema,
    strokeCancelledBroadcastSchema,
    whiteboardClearBroadcastSchema,
    whiteboardImagepasteSchema,
    whiteboardRegionSchema,
    whiteboardRegionCancelledSchema,
    whiteboardBackgroundSetBroadcastSchema,
    whiteboardRestoreSchema,
]);
/**
 * The full segment document as it lands in object storage.
 *
 * Invariants the writer maintains (not schema-enforced, see module header
 * for how readers should treat violations):
 *   - `fromSeq <= toSeq`
 *   - every `events[i].seq` is within `fromSeq..toSeq` and ascending
 *   - `events` MAY be empty: seq is shared with non-drawing broadcasts, so a
 *     span with no drawing event (e.g. a shutdown snapshot after chat-only
 *     activity) is still written as an `events: []` segment so the coverage
 *     stays contiguous. An empty segment covers `fromSeq..toSeq` exactly like
 *     a non-empty one — readers must judge coverage by the range, never by
 *     `events.length`.
 *
 * `.loose()` so future additive fields round-trip through older readers
 * (same forward-compat policy as `snapshotMetaSchema`).
 */
export const strokeLogSegmentSchema = z
    .object({
    version: z.literal(STROKE_LOG_SEGMENT_VERSION),
    roomId: z.string().min(1),
    /** First seq covered by this segment (inclusive, >= 1). */
    fromSeq: z.number().int().positive(),
    /** Last seq covered by this segment (inclusive). */
    toSeq: z.number().int().positive(),
    /**
     * Epoch ms when the server serialised this segment. Used to resolve
     * overlapping coverage ranges after a server restart (larger wins —
     * see module header) and for ops forensics on gaps.
     */
    flushedAtMs: z.number().int().nonnegative(),
    /**
     * What triggered the flush:
     *   - `'snapshot'` : snapshot publish trimmed the session buffer
     *   - `'final'`    : graceful shutdown flushed the residual tail
     *   - `'live'`     : NOT written to object storage — the client tags a
     *                    synthetic segment it built from a
     *                    `whiteboard.stroke-log-tail` WS response (the
     *                    un-flushed live tail of the session buffer) with this
     *                    so `mergeSegments` and diagnostics can tell it apart
     *                    from durable segments (絵ログ live tail,
     *                    `events/strokeLogTail.ts`). A reader will never
     *                    encounter `'live'` in an R2 object.
     *   - `'backfill'` : an empty (`events: []`) segment written after the fact
     *                    by the one-off repair script to fill a pre-existing
     *                    coverage hole (spans that earlier writers skipped).
     *                    Replay semantics are identical to any other segment.
     * Diagnostic only — replay semantics are identical.
     */
    flushReason: z.enum(['snapshot', 'final', 'live', 'backfill']),
    events: z.array(strokeLogEventSchema),
})
    .loose();
//# sourceMappingURL=strokeLog.js.map